import React from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Flag, LayoutGrid, MountainSnow, Route } from "lucide-react";
import type { Collection } from "../api/collections";
import { usePageMeta } from "../hooks/usePageMeta";
import { JsonLd } from "../components/JsonLd";
import { SiteSearch } from "../components/SiteSearch";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { ImageCredit } from "../components/ImageCredit";
import { companionNote } from "@atlas/shared";

/**
 * Signed-out view of /collections and its filtered aliases /hills, /trails
 * and /landmarks (ATL-132): a public, crawlable directory of every curated
 * collection with counts and one-line descriptions, plus the site search.
 * No progress, no 0% rings - that view (CollectionsPage) is for people with
 * an account. The prerendered HTML in scripts/prerender.mjs mirrors the copy
 * here; keep the two in step.
 */

export type DirectoryFilter = "all" | "peaks" | "trails" | "landmarks";

interface FilterMeta {
  path: "/collections" | "/hills" | "/trails" | "/landmarks";
  chip: string;
  eyebrow: string;
  heading: string;
  lede: string;
  title: string;
  description: string;
  unit: string;
}

export const DIRECTORY_META: Record<DirectoryFilter, FilterMeta> = {
  all: {
    path: "/collections",
    chip: "All",
    eyebrow: "Collections · United Kingdom",
    heading: "Every list worth finishing",
    lede:
      "Hill lists, long-distance trails and sets of landmarks, each one a checklist Atlas fills in from the walks you have already recorded. Browse them here, or search for a single peak.",
    title: "Collections - hill lists, trails and landmarks of the UK | Atlas",
    description:
      "Every collection Atlas tracks: the Wainwrights, Munros, Corbetts, county tops, National Trails, trig points and more, with the full list of what each one contains.",
    unit: "items",
  },
  peaks: {
    path: "/hills",
    chip: "Hill lists",
    eyebrow: "Hill lists · United Kingdom",
    heading: "The hill lists of Britain and Ireland",
    lede:
      "From the 214 Wainwrights to the 282 Munros, every classic hill list with its full set of summits. Open a list to see each peak, its height and where it sits.",
    title: "Hill lists of the UK - Wainwrights, Munros, Corbetts, county tops and more | Atlas",
    description:
      "Every hill list Atlas tracks with its complete set of summits: Wainwrights, Munros, Munro Tops, Corbetts, Fionas, Donalds, Nuttalls, Hewitts, Birketts, Marilyns, county tops and more.",
    unit: "peaks",
  },
  trails: {
    path: "/trails",
    chip: "Trails",
    eyebrow: "Trails · United Kingdom",
    heading: "Long-distance trails, section by section",
    lede:
      "National Trails, Scotland's Great Trails and the classic routes between. Atlas splits each one into sections and marks them walked as your GPS tracks cover them.",
    title: "Long-distance trails of the UK - National Trails and classic routes | Atlas",
    description:
      "Every long-distance trail Atlas tracks, split into sections: the Pennine Way, South West Coast Path, West Highland Way, Coast to Coast, Wales Coast Path and more.",
    unit: "trails",
  },
  landmarks: {
    path: "/landmarks",
    chip: "Landmarks",
    eyebrow: "Landmarks · United Kingdom",
    heading: "Landmarks worth the detour",
    lede:
      "Trig pillars, waterfalls, bothies, stone circles and the other places a walk tends to pass. Each set lists every landmark Atlas knows about.",
    title: "Landmark collections - trig points, waterfalls, bothies and stone circles | Atlas",
    description:
      "Every landmark set Atlas tracks: Ordnance Survey trig pillars, waterfalls, bothies, mountain huts, viewpoints, stone circles, bridges and caves across the UK.",
    unit: "landmarks",
  },
};

const GROUPS: Exclude<DirectoryFilter, "all">[] = ["peaks", "trails", "landmarks"];

const TYPE_ICON: Record<string, React.ReactNode> = {
  peaks: <MountainSnow size={22} />,
  trails: <Route size={22} />,
  landmarks: <Flag size={22} />,
};

const TYPE_LABEL: Record<string, string> = {
  peaks: "Hill list",
  trails: "Trail",
  landmarks: "Landmarks",
};

const CSS = `
.atlas-dir{ width:100%; max-width:960px; margin:0 auto; display:flex; flex-direction:column; gap:28px; }
.atlas-dir__hero{
  position:relative; border-radius:var(--radius-xl);
  border:1px solid var(--border-gold); padding:36px 32px 32px;
  box-shadow:var(--glow-gold-sm), var(--ring-top);
}
.atlas-dir__h1{
  font-family:var(--font-display); font-weight:700; letter-spacing:-.02em; line-height:1.05;
  font-size:clamp(30px, 4.6vw, 46px); color:var(--text-primary); margin:10px 0 12px;
}
.atlas-dir__lede{ max-width:640px; color:var(--text-secondary); font-size:var(--text-lg); line-height:var(--leading-relaxed); margin:0 0 24px; }
.atlas-dir__chips{ display:flex; gap:6px; padding:4px; background:var(--surface-card); border:1px solid var(--border-subtle); border-radius:var(--radius-pill); }
.atlas-dir__chip{
  display:flex; align-items:center; gap:7px; padding:0 16px; height:34px; border-radius:var(--radius-pill);
  font-family:var(--font-sans); font-size:13px; font-weight:600; text-decoration:none;
  color:var(--text-secondary); transition:var(--t-colors);
}
.atlas-dir__chip:hover{ color:var(--text-primary); }
.atlas-dir__chip[aria-current="page"]{ background:var(--accent); color:var(--text-on-gold); box-shadow:var(--glow-gold-sm); }
.atlas-dir__chip b{ font-family:var(--font-mono); font-size:10.5px; font-weight:700; padding:1px 6px; border-radius:var(--radius-pill); background:var(--surface-sunken); color:var(--text-muted); }
.atlas-dir__chip[aria-current="page"] b{ background:rgba(0,0,0,.16); color:var(--text-on-gold); }
.atlas-dir__section{ display:flex; flex-direction:column; gap:14px; }
.atlas-dir__section-head{ display:flex; align-items:baseline; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.atlas-dir__h2{ font-family:var(--font-display); font-weight:700; font-size:22px; letter-spacing:-.01em; color:var(--text-primary); margin:0; }
.atlas-dir__grid{ display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:14px; }

/* Cover-led cards: the image is the card. Text sits on a gradient at the
   foot so the photo stays the thing you look at (the covers were chosen
   one by one; give them the room). */
.atlas-dir__card{
  position:relative; display:flex; flex-direction:column; justify-content:flex-end;
  min-height:248px; padding:18px; text-decoration:none; color:#fff; overflow:hidden;
  border-radius:var(--radius-lg); border:1px solid rgba(255,255,255,.08);
  background-color:var(--surface-sunken); background-size:cover; background-position:center;
  background-clip:padding-box; /* the photo must not show through the translucent border */
  box-shadow:var(--shadow-sm);
  transition:transform var(--dur-quick) var(--ease-out), border-color var(--t-colors), box-shadow var(--t-colors);
}
.atlas-dir__card::before{
  content:""; position:absolute; inset:0;
  background:
    linear-gradient(180deg, rgba(9,12,16,.08) 0%, rgba(9,12,16,.18) 35%, rgba(9,12,16,.72) 62%, rgba(9,12,16,.96) 100%);
}
.atlas-dir__card:hover{ transform:translateY(-2px); border-color:var(--border-gold); box-shadow:var(--glow-gold-sm); }
.atlas-dir__card--plain{ justify-content:flex-start; color:var(--text-primary); background:linear-gradient(150deg, var(--surface-raised), var(--surface-sunken)); }
.atlas-dir__card--plain::before{ display:none; }
.atlas-dir__card--plain .atlas-dir__type{ color:var(--text-muted); }
.atlas-dir__card--plain .atlas-dir__desc{ color:var(--text-secondary); }
.atlas-dir__card--plain .atlas-dir__count{ color:var(--text-primary); }
.atlas-dir__plain-icon{ display:grid; place-items:center; width:44px; height:44px; margin-bottom:auto; border-radius:var(--radius-md); background:var(--accent-soft); border:1px solid var(--border-gold); color:var(--gold-400); }
.atlas-dir__body{ position:relative; z-index:1; display:flex; flex-direction:column; gap:5px; min-width:0; }
.atlas-dir__type{ font-family:var(--font-mono); font-size:10px; text-transform:uppercase; letter-spacing:.12em; color:rgba(255,255,255,.78); }
.atlas-dir__name{ font-family:var(--font-display); font-weight:700; font-size:22px; letter-spacing:-.015em; line-height:1.15; text-shadow:0 1px 3px rgba(0,0,0,.5); }
.atlas-dir__count{ font-family:var(--font-mono); font-size:12px; font-variant-numeric:tabular-nums; text-shadow:0 1px 3px rgba(0,0,0,.5); }
.atlas-dir__count b{ color:var(--gold-300); font-weight:700; }
.atlas-dir__desc{
  font-size:13.5px; line-height:1.5; color:rgba(255,255,255,.86); margin:3px 0 0; text-shadow:0 1px 2px rgba(0,0,0,.6);
  overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;
}
.atlas-dir__cta{
  display:flex; align-items:center; justify-content:space-between; gap:20px; flex-wrap:wrap;
  padding:24px 28px; border-radius:var(--radius-xl); background:var(--surface-card);
  border:1px solid var(--border-subtle); box-shadow:var(--shadow-sm), var(--ring-top);
}
.atlas-dir__cta h2{ font-family:var(--font-display); font-weight:700; font-size:20px; letter-spacing:-.01em; color:var(--text-primary); margin:0 0 6px; }
.atlas-dir__cta p{ margin:0; color:var(--text-secondary); font-size:14.5px; line-height:1.55; max-width:560px; }
.atlas-dir__cta-btn{
  display:inline-flex; align-items:center; gap:8px; height:var(--control-md); padding:0 20px; border-radius:var(--radius-md);
  background:var(--accent); color:var(--text-on-gold); font-weight:600; font-size:14px; text-decoration:none; white-space:nowrap;
}
@media (max-width:760px){
  .atlas-dir__hero{ padding:26px 18px 22px; }
  .atlas-dir__grid{ grid-template-columns:minmax(0,1fr); }
  .atlas-dir__card{ min-height:200px; }
}
`;

function ensure() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-dir-css")) {
    const s = document.createElement("style");
    s.id = "atlas-dir-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

const fmt = (n: number) => n.toLocaleString("en-GB");

/**
 * Hill lists keep the API's order, which signed out is the seed's curated
 * `sort_order` (Wainwrights, Munros, Yorkshire Three Peaks, ... the most
 * recognisable lists first; edit ALL_PEAK_COLLECTIONS in the geo seed to
 * change it). Trails by length, landmark sets as seeded. The prerender's
 * sortDirectory() mirrors this.
 */
export function sortForDirectory(items: Collection[], kind: DirectoryFilter): Collection[] {
  const out = [...items];
  if (kind === "trails") {
    out.sort((a, b) => (b.distanceKm ?? 0) - (a.distanceKm ?? 0) || a.name.localeCompare(b.name));
  }
  return out;
}

/** "214 peaks", "268 km", or nothing for a one-trail collection. */
function cardMeta(c: Collection): React.ReactNode {
  if (c.distanceKm != null) return <><b>{fmt(Math.round(c.distanceKm))}</b> km</>;
  if (c.type === "trails" && c.itemCount === 1) return null;
  const unit = DIRECTORY_META[(c.type as DirectoryFilter) in DIRECTORY_META ? (c.type as DirectoryFilter) : "all"].unit;
  return <><b>{fmt(c.itemCount)}</b> {unit}</>;
}

function DirectoryCard({ c, note }: { c: Collection; note?: string | null }) {
  const where = c.region ?? c.country;
  const base = cardMeta(c);
  // ATL-168: "89 peaks + 52 tops" when companion lists sit beside this one.
  const meta = base && note ? <>{base} {note}</> : base;
  const body = (
    <span className="atlas-dir__body">
      <span className="atlas-dir__type">{TYPE_LABEL[c.type] ?? "Collection"}</span>
      <span className="atlas-dir__name">{c.name}</span>
      {(meta || where) && (
        <span className="atlas-dir__count">
          {meta}
          {meta && where ? " · " : ""}
          {where}
        </span>
      )}
      {c.description && <p className="atlas-dir__desc">{c.description}</p>}
    </span>
  );
  const className = ["atlas-dir__card", c.coverImage ? "" : "atlas-dir__card--plain"].filter(Boolean).join(" ");
  const style = c.coverImage ? { backgroundImage: `url("${c.coverImage}")` } : undefined;
  const icon = !c.coverImage && <span className="atlas-dir__plain-icon">{TYPE_ICON[c.type] ?? <LayoutGrid size={22} />}</span>;
  // A collection that is exactly one trail links straight to the trail page.
  const credit = c.coverImage ? <ImageCredit src={c.coverImage} placement="corner" /> : null;
  return c.trailSlug ? (
    <Link to="/trails/$id" params={{ id: c.trailSlug }} className={className} style={style}>
      {credit}
      {icon}
      {body}
    </Link>
  ) : (
    <Link to="/collections/$slug" params={{ slug: c.slug }} className={className} style={style}>
      {credit}
      {icon}
      {body}
    </Link>
  );
}

export function CollectionsDirectory({
  filter,
  collections,
  allCollections,
  isLoading,
}: {
  filter: DirectoryFilter;
  collections: Collection[];
  /** Unfiltered list incl. companion lists, for the "+ 52 tops" notes (ATL-168). */
  allCollections?: Collection[];
  isLoading: boolean;
}) {
  ensure();
  const meta = DIRECTORY_META[filter];
  const byType = (t: string) => sortForDirectory(collections.filter((c) => c.type === t), t as DirectoryFilter);
  const visible = GROUPS.flatMap((g) => byType(g));
  const shown = filter === "all" ? visible : byType(filter);

  usePageMeta({
    title: meta.title,
    description: meta.description,
    canonicalPath: meta.path,
    enabled: !isLoading,
  });

  const counts: Record<DirectoryFilter, number> = {
    all: visible.length,
    peaks: byType("peaks").length,
    trails: byType("trails").length,
    landmarks: byType("landmarks").length,
  };

  const sections: { key: DirectoryFilter; items: Collection[] }[] =
    filter === "all" ? GROUPS.map((g) => ({ key: g, items: byType(g) })).filter((s) => s.items.length > 0) : [{ key: filter, items: shown }];

  return (
    <div className="r-page" style={{ padding: "36px 28px 56px", overflow: "auto", flex: 1 }}>
      <div className="atlas-dir">
      {!isLoading && shown.length > 0 && (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: meta.heading,
            url: `https://goatlas.co.uk${meta.path}`,
            numberOfItems: shown.length,
            itemListElement: shown.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: c.name,
              url: `https://goatlas.co.uk${c.trailSlug ? `/trails/${c.trailSlug}` : `/collections/${c.slug}`}`,
            })),
          }}
        />
      )}

      <header className="atlas-topo atlas-dir__hero">
        <p className="eyebrow" style={{ margin: 0 }}>
          {meta.eyebrow}
        </p>
        <h1 className="atlas-dir__h1">{meta.heading}</h1>
        <p className="atlas-dir__lede">{meta.lede}</p>
        <div style={{ maxWidth: 640 }}>
          <SiteSearch size="lg" />
        </div>
        <nav aria-label="Collection type" className="r-wrap" style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 22 }}>
          <div className="atlas-dir__chips r-chip-scroll">
            {(["all", "peaks", "trails", "landmarks"] as DirectoryFilter[]).map((f) => (
              <Link
                key={f}
                to={DIRECTORY_META[f].path}
                className="atlas-dir__chip"
                aria-current={f === filter ? "page" : undefined}
              >
                {DIRECTORY_META[f].chip}
                {!isLoading && <b>{counts[f]}</b>}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      {isLoading ? (
        <div className="atlas-dir__grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skel key={i} height="118px" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState icon={<LayoutGrid size={18} />} title="Nothing here yet" hint="This part of the directory is still being written." />
      ) : (
        sections.map((s) => (
          <section key={s.key} className="atlas-dir__section" aria-labelledby={`dir-${s.key}`}>
            {filter === "all" && (
              <div className="atlas-dir__section-head">
                <h2 id={`dir-${s.key}`} className="atlas-dir__h2">
                  {DIRECTORY_META[s.key].chip}
                  <span style={{ color: "var(--text-muted)", fontWeight: 500, marginLeft: 10, fontSize: 15 }}>{s.items.length}</span>
                </h2>
                <Link
                  to={DIRECTORY_META[s.key].path}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 600, color: "var(--text-accent)", textDecoration: "none" }}
                >
                  All {DIRECTORY_META[s.key].chip.toLowerCase()} <ArrowRight size={14} />
                </Link>
              </div>
            )}
            <div className="atlas-dir__grid">
              {s.items.map((c) => (
                <DirectoryCard key={c.id} c={c} note={companionNote(c.slug, allCollections ?? collections)} />
              ))}
            </div>
          </section>
        ))
      )}

      <aside className="atlas-dir__cta">
        <div>
          <h2>Connect once and every list fills itself in</h2>
          <p>
            Atlas matches the GPS history you already have (Strava, GPX files, Apple Health, Health Connect) against every
            peak, trail and landmark here. Nothing to record, nothing to plan.
          </p>
        </div>
        <Link to="/signup" className="atlas-dir__cta-btn">
          Create a free account <ArrowRight size={16} />
        </Link>
      </aside>
      </div>
    </div>
  );
}
